import { computed, ref, type Ref, type ComputedRef } from "vue";
import {
  isJust,
  isNothing,
  just,
  mapMaybe,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import type { ZwAct, ZwChapter, ZwScene } from "../lib/zwConfig";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { usePointerRowDrag } from "./usePointerRowDrag";

/** What's being dragged — carries `folder`/`sceneFile` directly rather than a
 * path string, since a chapter's own row and its first scene's row otherwise
 * share an identical path (both are that chapter's first scene path). */
export type DragSource = {
  key: string;
  kind: "act" | "chapter" | "scene" | "map";
  folder: string;
  sceneFile: Maybe<string>;
};

/** `actFolder` is the act a row sits under, or Nothing for ungrouped rows. Drop
 * targets need it to know which list a chapter is landing in. */
export type OutlineRow =
  | { kind: "act"; act: ZwAct; path: string }
  | {
      kind: "chapter";
      chapter: ZwChapter;
      actFolder: Maybe<string>;
      path: string;
    }
  | {
      kind: "scene";
      chapter: ZwChapter;
      actFolder: Maybe<string>;
      scene: ZwScene;
      index: number;
      path: string;
    }
  | {
      kind: "map";
      id: string;
      title: string;
      mapPath: string;
    };

type RowHit = {
  key: string;
  kind: "act" | "chapter" | "scene" | "map";
  folder: string;
  actFolder: Maybe<string>;
  sceneFile: Maybe<string>;
};

/** Reserved drop-target value for "drop a scene here to create a new chapter" —
 * flows through the same `[data-row-key]` hit-testing as every real row. */
export const NEW_CHAPTER_DROP_TARGET = "__new_chapter__";

/** Reserved drop target for "past the last row, ungrouped". Without it, an act
 * at the end of the manuscript would swallow every attempt to move something
 * below it, since the only rows down there belong to that act. */
export const END_OF_MANUSCRIPT_DROP_TARGET = "__end_of_manuscript__";

/**
 * Pointer-based reorder for the outline tree — see `usePointerRowDrag` for the
 * shared drag model. Dropping on a row means "take this row's slot"; dropping
 * on an act means "append to this act". Landing on the very last slot is still
 * possible: drop on the chapter's own header row (see `commitSceneDrop`) for a
 * scene, or the trailing drop zone for a chapter or act.
 */
export function useOutlineDrag(options: {
  chapters: Ref<ZwChapter[]>;
  outlineRows: Ref<OutlineRow[]>;
  rowKey: (row: OutlineRow) => string;
  chapterPrimaryPath: (chapter: ZwChapter) => string;
  collapsedChapters: Ref<string[]>;
  setCollapsedChapters: (next: string[]) => void;
  /** Opens the "new file" dialog — used when a scene is dropped on the
   * "move to new chapter" zone. The dialog's own commit flow reads
   * `pendingSceneDrop` back out once the new chapter exists. */
  openNewChapterDialog: () => void;
}): {
  dragFrom: Ref<Maybe<DragSource>>;
  dragOver: Ref<Maybe<string>>;
  dragKind: ComputedRef<Maybe<"act" | "chapter" | "scene" | "map">>;
  pendingSceneDrop: Ref<Maybe<{ folder: string; sceneFile: string }>>;
  onRowPointerDown: (source: DragSource, event: PointerEvent) => void;
  cleanup: () => void;
} {
  const workspace = useProjectWorkspaceStore();

  const pendingSceneDrop =
    ref<Maybe<{ folder: string; sceneFile: string }>>(nothing);

  function rowAtPoint(clientX: number, clientY: number): Maybe<RowHit> {
    const candidates = document.querySelectorAll<HTMLElement>("[data-row-key]");
    for (const el of candidates) {
      const rect = el.getBoundingClientRect();
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        continue;
      }
      const key = toMaybe(el.dataset.rowKey);
      const folder = toMaybe(el.dataset.chapterFolder);
      if (
        isNothing(key) ||
        key.value.length === 0 ||
        isNothing(folder) ||
        folder.value.length === 0
      ) {
        return nothing;
      }
      const kind =
        el.dataset.rowKind === "scene"
          ? "scene"
          : el.dataset.rowKind === "act"
            ? "act"
            : el.dataset.rowKind === "map"
              ? "map"
              : "chapter";
      const actFolderAttr = toMaybe(el.dataset.actFolder);
      return just({
        key: key.value,
        kind,
        folder: folder.value,
        actFolder: matchMaybe(actFolderAttr, {
          just: (attr) => (attr.length > 0 ? just(attr) : nothing),
          nothing: () => nothing,
        }),
        sceneFile: kind === "scene" ? toMaybe(el.dataset.sceneFile) : nothing,
      });
    }
    return nothing;
  }

  /** Fallback resolution when a drag ends without fresh pointer coordinates
   * (`pointercancel`) — looks the last-known hovered key up in the current row
   * list instead of a DOM lookup. */
  function rowHitFromKey(key: string): Maybe<RowHit> {
    if (
      key === NEW_CHAPTER_DROP_TARGET ||
      key === END_OF_MANUSCRIPT_DROP_TARGET
    ) {
      return just({
        key,
        kind: "chapter",
        folder: key,
        actFolder: nothing,
        sceneFile: nothing,
      });
    }
    const row = toMaybe(
      options.outlineRows.value.find((r) => options.rowKey(r) === key),
    );
    if (isNothing(row)) return nothing;
    const hit = row.value;
    if (hit.kind === "act") {
      return just({
        key,
        kind: "act",
        folder: hit.act.folder,
        actFolder: nothing,
        sceneFile: nothing,
      });
    }
    if (hit.kind === "map") {
      return just({
        key,
        kind: "map",
        folder: hit.id,
        actFolder: nothing,
        sceneFile: nothing,
      });
    }
    return just({
      key,
      kind: hit.kind,
      folder: hit.chapter.folder,
      actFolder: hit.actFolder,
      sceneFile: hit.kind === "scene" ? just(hit.scene.file) : nothing,
    });
  }

  /** The top-level item a drop target belongs to. An act's own row is its own
   * span; anything inside an act resolves to that act. Acts only ever move
   * between top-level slots, so that's the granularity they need. */
  function topLevelFolder(to: RowHit): Maybe<string> {
    if (to.key === END_OF_MANUSCRIPT_DROP_TARGET) return nothing;
    if (to.kind === "act") return just(to.folder);
    return isJust(to.actFolder) ? to.actFolder : just(to.folder);
  }

  function commitActDrop(from: DragSource, to: RowHit): void {
    const beforeM = topLevelFolder(to);
    if (isJust(beforeM) && beforeM.value === from.folder) return;
    void workspace.onMoveAct(from.folder, beforeM);
  }

  function commitMapDrop(from: DragSource, to: RowHit): void {
    if (to.key === END_OF_MANUSCRIPT_DROP_TARGET) {
      void workspace.onMoveMap(from.folder, nothing);
      return;
    }
    // Maps stay at top level; dropping onto an act means "before that act".
    const beforeM = topLevelFolder(to);
    if (isJust(beforeM) && beforeM.value === from.folder) return;
    void workspace.onMoveMap(from.folder, beforeM);
  }

  function commitChapterDrop(from: DragSource, to: RowHit): void {
    if (to.key === END_OF_MANUSCRIPT_DROP_TARGET) {
      void workspace.onMoveChapterToSlot(from.folder, {
        actFolder: nothing,
        beforeFolder: nothing,
      });
      return;
    }
    // Dropping onto an act's own row appends to it. That's the only gesture
    // that can put a chapter at the end of an act, so it can't also mean
    // "before the act".
    if (to.kind === "act") {
      void workspace.onMoveChapterToSlot(from.folder, {
        actFolder: just(to.folder),
        beforeFolder: nothing,
      });
      return;
    }
    // Dropping onto a map takes that map's top-level slot.
    if (to.kind === "map") {
      void workspace.onMoveChapterToSlot(from.folder, {
        actFolder: nothing,
        beforeFolder: just(to.folder),
      });
      return;
    }
    if (to.folder === from.folder) return;
    void workspace.onMoveChapterToSlot(from.folder, {
      actFolder: to.actFolder,
      beforeFolder: just(to.folder),
    });
  }

  function commitSceneDrop(from: DragSource, to: RowHit): void {
    if (isNothing(from.sceneFile)) return;
    const fromFolder = from.folder;
    const fromSceneFile = from.sceneFile.value;

    if (to.folder === NEW_CHAPTER_DROP_TARGET) {
      pendingSceneDrop.value = just({
        folder: fromFolder,
        sceneFile: fromSceneFile,
      });
      options.openNewChapterDialog();
      return;
    }
    // Acts and maps hold no prose, so they're inert for scene drags.
    if (
      to.kind === "act" ||
      to.kind === "map" ||
      to.key === END_OF_MANUSCRIPT_DROP_TARGET
    ) {
      return;
    }

    if (
      fromFolder === to.folder &&
      isJust(to.sceneFile) &&
      to.sceneFile.value === fromSceneFile
    ) {
      return;
    }

    const toSceneFile = to.kind === "chapter" ? nothing : to.sceneFile;
    const destinationHadOneScene =
      options.chapters.value.find((c) => c.folder === to.folder)?.scenes
        .length === 1;

    void workspace
      .onReorderProjectScenes(
        { folder: fromFolder, sceneFile: fromSceneFile },
        {
          folder: to.folder,
          sceneFile: toSceneFile,
        },
      )
      .then(() => {
        if (destinationHadOneScene === false) return;
        const chapter = toMaybe(
          options.chapters.value.find((c) => c.folder === to.folder),
        );
        if (isNothing(chapter)) return;
        const path = options.chapterPrimaryPath(chapter.value);
        if (options.collapsedChapters.value.includes(path) === true) {
          options.setCollapsedChapters(
            options.collapsedChapters.value.filter((p) => p !== path),
          );
        }
      });
  }

  const drag = usePointerRowDrag<DragSource>({
    rowAtPoint(clientX, clientY) {
      return mapMaybe(rowAtPoint(clientX, clientY), (hit) => hit.key);
    },
    shouldIgnorePointerDown(target): boolean {
      return (
        target.closest(".file-twist, .file-add-scene, .act-twist") !== null
      );
    },
    onDrop(from, toKey): void {
      const hitM = rowHitFromKey(toKey);
      if (isNothing(hitM)) return;
      const hit = hitM.value;
      if (from.kind === "scene") commitSceneDrop(from, hit);
      else if (from.kind === "act") commitActDrop(from, hit);
      else if (from.kind === "map") commitMapDrop(from, hit);
      else commitChapterDrop(from, hit);
    },
  });

  const dragKind = computed(() => mapMaybe(drag.dragFrom.value, (f) => f.kind));

  return {
    dragFrom: drag.dragFrom,
    dragOver: drag.dragOver,
    dragKind,
    pendingSceneDrop,
    onRowPointerDown: drag.onRowPointerDown,
    cleanup: drag.cleanup,
  };
}
